import type { UseFormReturn } from 'react-hook-form';

import { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useUser } from '~/api/user';
import { Field, Select } from '~/components/Form';

type FormWithBrand = {
  brandId: number | null;
};

type Props<T extends FormWithBrand> = {
  form: T extends FormWithBrand ? UseFormReturn<T> : never;
};

export const FormBrand = <T extends FormWithBrand>({ form: { control } }: Props<T>) => {
  const { data: user } = useUser();

  const brandOptions = useMemo(() => user.brands.map((brand) => ({ value: brand.id, label: brand.companyName })), [user.brands]);

  const { t } = useTranslation(['invoices', 'validation']);

  if (brandOptions.length <= 1) return null;

  return (
    <Controller
      control={control}
      name="brandId"
      render={({ field: { name, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
        <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
          <Field.Label>{t('invoices:fields.brandId.label')}</Field.Label>

          <Select.Root items={brandOptions} onValueChange={onChange} value={value}>
            <Select.Button placeholder={t('invoices:fields.brandId.placeholder')} ref={ref} />
            <Select.List>
              {brandOptions.map((option) => (
                <Select.Item key={option.value} value={option.value}>
                  {option.label}
                </Select.Item>
              ))}
            </Select.List>
          </Select.Root>

          <Field.Error match={!!error}>{error?.message}</Field.Error>
        </Field.Root>
      )}
      rules={{ required: { value: true, message: t('validation:required') } }}
    />
  );
};
